import React, { useState } from "react";
import { FaEnvelope, FaTrash } from "react-icons/fa";
import { useParams } from "react-router-dom";
import { FaCirclePlus, FaGlobe } from "react-icons/fa6";
import ModalButton from "../../../../components/common/form/ModalButton";
import AddEditContactForm from "../../../../components/common/form/AddEditContactForm";
import { handleRTKQuery } from "../../../../helpers/utils/RtkQueryUtils";
import {
    useCreateContactMutation,
    useDeleteContactsByIdMutation,
    useGetContactByRelationIdQuery,
    useUpdateContactMutation
} from "../../../../components/common/form/AddEditContactApi";
import ConfirmationModal from "../../../../components/layout/modal/ConfirmationModal";
import { hasPermission } from "../../../../helpers/utils/AccessControlUtils";

const AssociationContactForm = () => {

    let { associationId } = useParams();
    const { data: collectorContactData } = useGetContactByRelationIdQuery(associationId);
    const iconButtonStyle = "cursor-pointer text-surface-0 text-md";
    const [show, setShow] = useState(false);
    const [contactData, setContactData] = useState<any>({});
    const [updateContactApi, updateContactApiResponse] = useUpdateContactMutation();
    const [createContactApi, createContactApiResponse] = useCreateContactMutation();
    const [contactId, setContactId] = useState<any>(null);
    const [deleteContacts, { isLoading }] = useDeleteContactsByIdMutation();

    const handleSubmit = async (e: any) => {
        const isCreate = !contactId;

        const requestBody = {
            id: isCreate ? null : contactData?.id,
            name: contactData?.name,
            email: contactData?.email,
            website: contactData?.website,
            phone: contactData?.phone,
            fax: contactData?.fax,
            preferredContactMethod: contactData?.preferredContactMethod?.value || null,
            isPrimary: contactData?.isPrimary,
            note: contactData?.note,
            address: {
                ...contactData?.address,
                state: contactData?.address?.state?.value || null
            }
        };

        await handleRTKQuery(
            async () => {
                if (isCreate) {
                    return await createContactApi({
                        contactId: associationId,
                        requestBody,
                        isSendLink: false,
                    }).unwrap();
                } else {
                    return await updateContactApi({
                        contactId,
                        requestBody,
                    }).unwrap();
                }
            },
            (isSuccess: boolean) => {
                if (isSuccess) {
                    setContactData({});
                    setShow(false);
                }
            }
        );
    };

    const handleRowClick = (contactId: any) => {
        setContactId(contactId)
        setShow(true);
    };

    const handleDelete = async (contactId: string) => {
        if (contactId) {
            await deleteContacts(contactId).unwrap();
        }
    };


    return (
        <div className="bg-text-1 shadow-[0_3px_10px_#0003] text-sm mt-3 rounded-md">
            <div className="flex justify-between items-center border-b border-[rgba(0,0,0,0.1)] p-2">
                <h5 className="font-semibold text-surface-0">Association Contacts
                    ({collectorContactData?.length || 0})</h5>
                {hasPermission(["ASSOC-U"]) &&
                    <ModalButton
                        triggerButton={<FaCirclePlus className={`${iconButtonStyle} !text-highlight-0`}
                            title={"Add Contact"} />}
                        modal={
                            <AddEditContactForm
                                formType={"Add"}
                                show={true}
                                setShow={() => {

                                }}
                                onSubmit={() => handleSubmit(contactData)}
                                setContactData={setContactData}
                                contactData={contactData}
                                isLoading={createContactApiResponse.isLoading}
                                overrideDismissMethod={() => {
                                    setContactData({});
                                }}
                                isReadOnly={hasPermission(["ASSOC-U"]) === false}
                            />
                        }
                    />
                }
            </div>
            <div className={'p-2 max-h-[150px] overflow-auto scrollbar-hide'}>
                <table className="w-full bg-text-0 border-2 border-[rgba(0,0,0,0.1)]">
                    <thead className="border-b-2 border-[rgba(0,0,0,0.1)]">
                        <tr>
                            {["Name", "Phone", "Email", "Website", "Address", "Action"].map((heading) => (
                                <th key={heading}
                                    className="p-2 text-left font-medium border-b border-surface-5 whitespace-nowrap">{heading}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {collectorContactData?.length === 0 ? (
                            <tr>
                                <td colSpan={16} className="text-gray-500 p-2">There are no contacts  to display</td>
                            </tr>
                        ) : collectorContactData?.map((contact: any) => (
                            <tr key={contact.id} className="bg-text-1">
                                <td
                                    className="p-2  text-highlight-0 cursor-pointer text-highlight-4"
                                    onClick={() => handleRowClick(contact.id)}>
                                    {contact.name}
                                </td>
                                <td className="p-2">{contact.phone ? contact.phone : "-"}</td>
                                <td className="p-2"> {contact?.email && <FaEnvelope title={contact?.email} />}</td>
                                <td className="px-4 py-2">
                                    {contact?.website ? (
                                        <a
                                            href={contact.website.startsWith("http") ? contact.website : `https://${contact.website}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-1  text-highlight-0 hover:underline"
                                        >
                                            <FaGlobe className="text-sm" />

                                        </a>
                                    ) :"-"}
                                </td>
                                <td className="p-2">{contact.address?.mailingAddress || "-"}</td>
                                <td className="px-4 py-2">
                                    <ModalButton
                                        triggerButton={<FaTrash className={iconButtonStyle}
                                            title={"Delete Note"} />}
                                        modal={
                                            <ConfirmationModal
                                                show={true}
                                                setShow={() => {
                                                }}
                                                title={"Delete Contact"}
                                                bodyMessage={"Are you sure you want to delete this contact?"}
                                                onSubmit={() => handleDelete(contact.id)}
                                                isLoading={isLoading}
                                            />
                                        }
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {show && (
                <AddEditContactForm
                    formType={"Update"}
                    show={true}
                    setShow={setShow}
                    contactId={contactId}
                    setContactData={setContactData}
                    contactData={contactData}
                    onSubmit={() => handleSubmit(contactData)}
                    isLoading={updateContactApiResponse?.isLoading}
                    overrideDismissMethod={() => {
                        setContactId(null);
                        setContactData({});
                    }}
                />
            )}
        </div>

    );
};

export default AssociationContactForm;
